import React, { useEffect, useRef } from 'react';
import { BaseStagePlugin, getFramework, useIdentity, useScale, useViewSize } from '@/framework';
import type { ShortcutMenu } from '@/plugins/shortcut-menu';
import { isHotKeyPress } from '@/utils/event-kit';
import { PluginType, type IExportPlugin } from '@/version-types';
import CropImgWrap from './components/crop-img-wrap';
import { CropImgPlugin } from './crop-img';
import cropImgPluginState, { useCropImgState } from './states/crop-img-state';

const CropImgView = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const scale = useScale();
    const { width: stageWidth, height: stageHeight } = useViewSize();
    const isCropImage = useCropImgState()((state) => state.isCropImage);
    const isAutoCutoutLayer = useCropImgState()((state) => state.isAutoCutoutLayer);
    const nodeRef = useRef<HTMLDivElement>(null);
    const wheelY = useRef<number>(0);

    useEffect(() => {
        const shortcutMenu = framework.service.registration.getInstance<ShortcutMenu>('ShortcutMenu');

        if (isCropImage) {
            shortcutMenu.setVisible(false);
        }

        return () => {
            shortcutMenu.setVisible(true);
        };
    }, [isCropImage]);

    // 裁剪的时候也要可以滚轮缩放
    useEffect(() => {
        if (!isCropImage) return;
        const mouseWheelHandle = (ev) => {
            const isScaleKeyDown = ev.ctrlKey || isHotKeyPress();
            if (!isScaleKeyDown) return;
            ev.preventDefault();
            wheelY.current += ev.deltaY;
            if (Math.abs(wheelY.current) >= 4) {
                if (ev.deltaY > 0) {
                    wheelY.current = 0;
                    framework.service.edgelessCanvas.zoomOut();
                } else if (ev.deltaY < 0) {
                    wheelY.current = 0;
                    framework.service.edgelessCanvas.zoomIn();
                }
            }
        };
        framework.service.registration.getInstance<CropImgPlugin>('CropImgPlugin').setRootTarget(nodeRef.current);
        nodeRef.current?.addEventListener('wheel', mouseWheelHandle);
        return () => {
            nodeRef.current?.removeEventListener('wheel', mouseWheelHandle);
        };
    }, [isCropImage]);

    if (!isCropImage) return null;

    return (
        <div className="crop-img-plugin-wrap" ref={nodeRef}>
            <BaseStagePlugin name="crop-img-plugin">
                <CropImgWrap
                    scale={scale}
                    stageWidth={stageWidth}
                    stageHeight={stageHeight}
                    framework={framework}
                    identity={identity}
                    isAutoCutoutLayer={isAutoCutoutLayer}
                />
            </BaseStagePlugin>
        </div>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 9,
    name: 'crop-img-plugin',
    render() {
        return <CropImgView />;
    },
    createInstance({ identity }) {
        return [{ key: 'CropImgPlugin', value: new CropImgPlugin(identity) }];
    },
    createState() {
        return [cropImgPluginState];
    },
    destroy({ getInstance }) {
        getInstance<CropImgPlugin>('CropImgPlugin').destroy();
    },
};

export default exportPlugin;
